import { useEffect, useRef, useState, useCallback } from 'react'
import { isDurationMismatch } from '../utils/videoUtils'

// ============================================================================
// PURE HELPER FUNCTIONS - Easy to test and debug in isolation
// ============================================================================

/**
 * Checks if the shot list structure has changed (new scenes/shots added, not just updates)
 * @param {Array} currentShotList - Current shot list from formData
 * @param {Array|null} lastShotList - Previous shot list reference
 * @returns {boolean} true if structure has changed
 */
export const hasStructureChanged = (currentShotList, lastShotList) => {
  if (!lastShotList) return true
  if (lastShotList.length !== currentShotList.length) return true

  return currentShotList.some((scene, idx) => {
    const lastScene = lastShotList[idx]
    if (!lastScene) return true
    if (lastScene.id !== scene.id) return true
    if ((scene.shots || []).length !== (lastScene.shots || []).length)
      return true
    return false
  })
}

/**
 * Merges calculated durations into shot list and identifies duration mismatches
 * @param {Array} currentShotList - Current shot list
 * @param {Array} calculatedShotList - Calculated shot list with new durations
 * @returns {{ mergedList: Array, mismatchedScenes: Array }}
 */
export const mergeDurationsIntoShotList = (
  currentShotList,
  calculatedShotList
) => {
  const mismatchedScenesMap = {} // { sceneId: { title, shots: [] } }

  const mergedList = currentShotList.map((scene, sIdx) => {
    // Fallback: match by ID, then by index
    const calculatedScene =
      calculatedShotList.find(s => s.id && s.id === scene.id) ||
      calculatedShotList[sIdx]

    if (!calculatedScene) return scene

    return {
      ...scene,
      shots: (scene.shots || []).map((shot, shIdx) => {
        // Fallback: match by ID, then by index
        const calculatedShot =
          (calculatedScene.shots || []).find(
            sh => sh.id && sh.id === shot.id
          ) || (calculatedScene.shots || [])[shIdx]

        if (!calculatedShot) return shot

        const currentDur = parseFloat(shot.video_duration || 0)
        const newDur = parseFloat(calculatedShot.video_duration || 0)

        if (isDurationMismatch(currentDur, newDur)) {
          const sceneKey = scene.id || `scene-${sIdx}`
          if (!mismatchedScenesMap[sceneKey]) {
            mismatchedScenesMap[sceneKey] = {
              title: scene.title || `Scene ${sIdx + 1}`,
              shots: []
            }
          }

          mismatchedScenesMap[sceneKey].shots.push({
            title: shot.title
              ? `Shot ${shIdx + 1}: ${shot.title}`
              : `Shot ${shIdx + 1}`,
            oldDuration: currentDur.toFixed(1),
            newDuration: newDur.toFixed(1)
          })

          return {
            ...shot,
            calculated_duration: newDur
          }
        }

        // If no mismatch but has an old calculated_duration, clear it
        if (shot.calculated_duration) {
          const { calculated_duration, ...rest } = shot
          return rest
        }

        return shot
      })
    }
  })

  const mismatchedScenes = Object.values(mismatchedScenesMap)

  return { mergedList, mismatchedScenes }
}

/**
 * Finds shots that need video (re)generation
 * @param {Array} currentShotList - Current shot list
 * @param {Array|null} calculatedShotList - Calculated shot list (optional)
 * @param {Array} videoGenerating - Array of currently generating videos
 * @returns {Array} Scenes with shots that need generation
 */
export const findShotsNeedingGeneration = (
  currentShotList,
  calculatedShotList,
  videoGenerating
) => {
  return currentShotList
    .map((scene, sIdx) => {
      const invalidShots = (scene.shots || []).filter((shot, shIdx) => {
        // 1. Skip if already generating
        const isCurrentlyGenerating = (videoGenerating || []).some(
          v =>
            (v.sceneId || '').toString().trim() ===
            (scene.id || '').toString().trim() &&
            (v.shotId || '').toString().trim() ===
            (shot.id || '').toString().trim()
        )
        if (isCurrentlyGenerating) return false

        // 2. Skip shots that have errors - let user manually retry them
        if (shot.errorGeneratingVideo) return false

        const hasVideo = !!shot.vidSrc

        // if shot has no video and is not generating, return true
        if (!hasVideo) {
          return true
        }

        // 3. Not generated yet - auto-generate if not a failed attempt
        if (!hasVideo && !shot.errorGeneratingVideo) {
          return true
        }

        // 4. Check for duration mismatch (if calculatedShotList exists)
        if (calculatedShotList) {
          const calculatedScene =
            calculatedShotList.find(s => s.id && s.id === scene.id) ||
            calculatedShotList[sIdx]

          const calculatedShot =
            calculatedScene?.shots?.find(sh => sh.id && sh.id === shot.id) ||
            calculatedScene?.shots?.[shIdx]

          if (calculatedShot) {
            const currentDur = parseFloat(shot.video_duration || 0)
            const newDur = parseFloat(calculatedShot.video_duration || 0)

            if (hasVideo && isDurationMismatch(currentDur, newDur)) {
              return true
            }
          }
        }

        return false
      })

      if (invalidShots.length > 0) {
        return {
          ...scene,
          shots: invalidShots.map((shot, shIdx) => {
            // Find the calculated duration for this shot to pass it to the generator
            const calculatedScene =
              calculatedShotList?.find(s => s.id && s.id === scene.id) ||
              calculatedShotList?.[sIdx]

            const calculatedShot =
              calculatedScene?.shots?.find(sh => sh.id && sh.id === shot.id) ||
              calculatedScene?.shots?.[shIdx]

            if (calculatedShot) {
              return {
                ...shot,
                calculated_duration: calculatedShot.video_duration
              }
            }
            return shot
          })
        }
      }
      return null
    })
    .filter(s => s !== null)
}

// ============================================================================
// DEBUG LOGGER - Structured logging for easier debugging
// ============================================================================

const createDebugLogger = (enabled = true) => {
  const prefix = '[AutoVideoGen]'

  return {
    state: data => {
      if (!enabled) return
      console.log(`${prefix} State:`, data)
    },
    action: (action, data = null) => {
      if (!enabled) return
      console.log(`${prefix} Action: ${action}`, data || '')
    },
    skip: reason => {
      if (!enabled) return
      console.log(`${prefix} Skipped: ${reason}`)
    }
  }
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Custom hook that manages automatic video generation logic
 *
 * Responsibilities:
 * 1. Detect when shotList structure changes
 * 2. Merge calculated durations into shotList
 * 3. Identify shots needing (re)generation
 * 4. Trigger auto-regeneration for new/changed shots
 *
 * @param {Object} options Hook options
 * @returns {Object} Hook state and controls
 */
export const useAutoVideoGeneration = ({
  formData,
  setFormData,
  videoProvider,
  videoGenerating = [],
  generatingVideoForAllScenes,
  isCalculatingDurations,
  handleGenerateFullVideo,
  debugEnabled = false
}) => {
  // Refs for tracking state across renders
  const lastShotListRef = useRef(null)
  const hasCheckedInitialVideosRef = useRef(false)
  const autoRegenerationTriggeredRef = useRef(false)

  // State for regeneration info modal
  const [affectedScenesByMismatch, setAffectedScenesByMismatch] = useState([])
  const [showRegenerationInfo, setShowRegenerationInfo] = useState(false)

  // Debug logger
  const debug = createDebugLogger(debugEnabled)

  // ===== MAIN ORCHESTRATING EFFECT =====
  useEffect(() => {
    const currentShotList = formData?.shotList || []
    const lastShotList = lastShotListRef.current
    const isDialogueMode = formData?.audioMode === 'dialogue'

    // Log current state for debugging
    debug.state({
      videoProvider: !!videoProvider,
      durationsReady: formData.durationsReady,
      hasCalculatedShotList: !!formData.calculatedShotList,
      isCalculatingDurations,
      generatingVideoForAllScenes,
      autoRegenerationTriggered: autoRegenerationTriggeredRef.current,
      hasCheckedInitialVideos: hasCheckedInitialVideosRef.current,
      shotListLength: currentShotList.length
    })

    // Early exit: No video provider and has duration task in progress
    if (!videoProvider && formData.durationTaskId) {
      debug.skip('No videoProvider and has durationTaskId')
      return
    }

    // Detect structure changes
    const isNewStructure = hasStructureChanged(currentShotList, lastShotList)

    // Reset check flags on provider change or structure change
    if (!videoProvider || isNewStructure) {
      hasCheckedInitialVideosRef.current = false
    }

    // Update the ref for next comparison
    lastShotListRef.current = currentShotList

    // ===== STEP 1: Merge calculated durations if ready =====
    const shouldMergeDurations =
      formData.calculatedShotList &&
      formData.durationsReady &&
      !isCalculatingDurations

    if (shouldMergeDurations) {
      debug.action('Merging durations')

      const { mergedList, mismatchedScenes } = mergeDurationsIntoShotList(
        currentShotList,
        formData.calculatedShotList
      )

      if (mismatchedScenes.length > 0) {
        debug.action('Duration mismatches found', mismatchedScenes)

        setFormData(prev => ({
          ...prev,
          shotList: mergedList,
          calculatedShotList: null
        }))
        setAffectedScenesByMismatch(mismatchedScenes)
        setShowRegenerationInfo(true)
        hasCheckedInitialVideosRef.current = false // Allow re-check in next cycle
      } else if (formData.calculatedShotList) {
        // Clear calculated list if no mismatches
        setFormData(prev => ({ ...prev, calculatedShotList: null }))
      }
    }

    // ===== STEP 2: Trigger auto-regeneration if needed =====
    const shouldTriggerRegeneration =
      currentShotList.length > 0 &&
      videoProvider &&
      (formData.durationsReady || isDialogueMode) &&
      !isCalculatingDurations &&
      !generatingVideoForAllScenes &&
      !autoRegenerationTriggeredRef.current &&
      !hasCheckedInitialVideosRef.current

    if (shouldTriggerRegeneration) {
      hasCheckedInitialVideosRef.current = true

      const shotsToProcess = findShotsNeedingGeneration(
        currentShotList,
        formData.calculatedShotList,
        videoGenerating
      )

      if (shotsToProcess.length > 0) {
        debug.action('Triggering auto-regeneration', {
          scenesCount: shotsToProcess.length,
          shotsToProcess
        })

        autoRegenerationTriggeredRef.current = true
        handleGenerateFullVideo(shotsToProcess)
      } else {
        debug.skip('No shots need regeneration')
      }
    }

    // ===== STEP 3: Reset trigger ref when generation completes =====
    if (autoRegenerationTriggeredRef.current && !generatingVideoForAllScenes) {
      debug.action('Resetting autoRegenerationTriggered ref')
      autoRegenerationTriggeredRef.current = false
    }

    // Reset check flag if dependencies change
    if (!videoProvider || isNewStructure || (!formData.durationsReady && !isDialogueMode)) {
      hasCheckedInitialVideosRef.current = false
      autoRegenerationTriggeredRef.current = false
    }
  }, [
    videoProvider,
    videoGenerating,
    generatingVideoForAllScenes,
    isCalculatingDurations,
    formData.durationsReady,
    formData?.shotList,
    formData?.audioMode
  ])

  // ===== RESET FUNCTION (for external use if needed) =====
  const resetAutoGeneration = useCallback(() => {
    hasCheckedInitialVideosRef.current = false
    autoRegenerationTriggeredRef.current = false
    lastShotListRef.current = null
  }, [])

  return {
    // Modal state
    affectedScenesByMismatch,
    showRegenerationInfo,
    setShowRegenerationInfo,

    // Reset function
    resetAutoGeneration
  }
}

export default useAutoVideoGeneration
